import React, { FC, useCallback, useEffect, useRef } from 'react';
import { Box, Divider, IconButton, Paper, Tooltip } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import TitleIcon from '@mui/icons-material/Title';
import DOMPurify from 'dompurify';

type RichTextEditorProps = {
    value: string;
    onChange: (html: string) => void;
    placeholder?: string;
    minHeight?: number;
};

type ToolbarButtonProps = {
    title: string;
    onClick: () => void;
    icon: React.ReactNode;
};

const ToolbarButton: FC<ToolbarButtonProps> = ({ title, onClick, icon }) => (
    <Tooltip title={title}>
        <IconButton size="small" onMouseDown={(event) => event.preventDefault()} onClick={onClick}>
            {icon}
        </IconButton>
    </Tooltip>
);

const RichTextEditor: FC<RichTextEditorProps> = ({ value, onChange, placeholder, minHeight = 200 }) => {
    const editorRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const editor = editorRef.current;
        if (!editor) return;
        if (document.activeElement === editor) return;
        const sanitized = DOMPurify.sanitize(value);
        if (editor.innerHTML !== sanitized) {
            editor.innerHTML = sanitized;
        }
    }, [value]);

    const handleInput = useCallback(() => {
        if (editorRef.current) {
            onChange(DOMPurify.sanitize(editorRef.current.innerHTML));
        }
    }, [onChange]);

    const handlePaste = useCallback(
        (event: React.ClipboardEvent<HTMLDivElement>) => {
            const clipboardHtml = event.clipboardData.getData('text/html');
            const clipboardText = event.clipboardData.getData('text/plain');
            const looksLikeHtml = /<[a-z][\s\S]*>/i.test(clipboardText);

            if (!clipboardHtml && looksLikeHtml) {
                event.preventDefault();
                document.execCommand('insertHTML', false, DOMPurify.sanitize(clipboardText));
                handleInput();
            }
        },
        [handleInput],
    );

    const execFormat = useCallback(
        (command: string, commandValue?: string) => {
            document.execCommand(command, false, commandValue);
            editorRef.current?.focus();
            handleInput();
        },
        [handleInput],
    );

    const handleHeading = useCallback((tag: string) => execFormat('formatBlock', tag), [execFormat]);

    return (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 0.25,
                    px: 1,
                    py: 0.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'grey.50',
                }}
            >
                <ToolbarButton
                    title="Bold"
                    onClick={() => execFormat('bold')}
                    icon={<FormatBoldIcon fontSize="small" />}
                />
                <ToolbarButton
                    title="Italic"
                    onClick={() => execFormat('italic')}
                    icon={<FormatItalicIcon fontSize="small" />}
                />
                <ToolbarButton
                    title="Underline"
                    onClick={() => execFormat('underline')}
                    icon={<FormatUnderlinedIcon fontSize="small" />}
                />
                <ToolbarButton
                    title="Strikethrough"
                    onClick={() => execFormat('strikeThrough')}
                    icon={<FormatStrikethroughIcon fontSize="small" />}
                />

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <ToolbarButton
                    title="Heading 1"
                    onClick={() => handleHeading('h1')}
                    icon={<TitleIcon fontSize="small" />}
                />
                <ToolbarButton
                    title="Heading 2"
                    onClick={() => handleHeading('h2')}
                    icon={<TitleIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />}
                />
                <ToolbarButton
                    title="Paragraph"
                    onClick={() => handleHeading('p')}
                    icon={
                        <Box component="span" sx={{ fontSize: '0.75rem', fontWeight: 'bold', lineHeight: 1 }}>
                            P
                        </Box>
                    }
                />

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <ToolbarButton
                    title="Bullet list"
                    onClick={() => execFormat('insertUnorderedList')}
                    icon={<FormatListBulletedIcon fontSize="small" />}
                />
                <ToolbarButton
                    title="Numbered list"
                    onClick={() => execFormat('insertOrderedList')}
                    icon={<FormatListNumberedIcon fontSize="small" />}
                />

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <ToolbarButton
                    title="Align left"
                    onClick={() => execFormat('justifyLeft')}
                    icon={<FormatAlignLeftIcon fontSize="small" />}
                />
                <ToolbarButton
                    title="Align center"
                    onClick={() => execFormat('justifyCenter')}
                    icon={<FormatAlignCenterIcon fontSize="small" />}
                />
                <ToolbarButton
                    title="Align right"
                    onClick={() => execFormat('justifyRight')}
                    icon={<FormatAlignRightIcon fontSize="small" />}
                />

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <ToolbarButton
                    title="Clear formatting"
                    onClick={() => execFormat('removeFormat')}
                    icon={<FormatClearIcon fontSize="small" />}
                />
            </Box>

            <Box
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onInput={handleInput}
                onPaste={handlePaste}
                data-placeholder={placeholder}
                sx={{
                    minHeight,
                    p: 2,
                    outline: 'none',
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                    '&:empty:before': {
                        content: 'attr(data-placeholder)',
                        color: 'text.disabled',
                        pointerEvents: 'none',
                    },
                    '& h1': { fontSize: '1.5rem', fontWeight: 'bold', margin: '0.5em 0' },
                    '& h2': { fontSize: '1.25rem', fontWeight: 'bold', margin: '0.5em 0' },
                    '& p': { margin: '0.25em 0' },
                    '& ul': { paddingLeft: '1.5rem', margin: '0.25em 0' },
                    '& ol': { paddingLeft: '1.5rem', margin: '0.25em 0' },
                }}
            />
        </Paper>
    );
};

export default RichTextEditor;
